import { StandardEvents } from '@shopify/events';
import { readState, remember, rankProducts, topCategory } from '@theme/industromart-interest-core';

const key = 'im-interests-v1';
let context = {};
try { context = JSON.parse(document.getElementById('IMInterestContext')?.textContent || '{}'); } catch {}
let state = readState(null);
try { state = readState(sessionStorage.getItem(key)); } catch {}
let request;
let generation = 0;
const observers = new Map();
const save = () => { try { sessionStorage.setItem(key, JSON.stringify(state)); } catch {} };
const record = (categories, type, value) => { remember(state, categories, type, value); save(); };

if (context.page === 'collection' && context.collection && context.collection !== 'all') record([context.collection], '', 2);
if (context.page === 'product') record(context.productCollections, context.productType, 3);

const explicitOrder = () => {
  const params = new URLSearchParams(location.search);
  return context.page === 'search' || (context.page === 'collection' &&
    [...params.keys()].some(name => name === 'sort_by' || name.startsWith('filter.')));
};

function reorderGrid(grid) {
  if (explicitOrder()) return;
  const products = [...grid.children].flatMap(item => {
    const card = item.querySelector('product-card[data-im-product-type]');
    return card ? [{ item, type: card.dataset.imProductType, categories: (card.dataset.imCollections || '').split('|'),
      available: card.dataset.imAvailable === 'true' }] : [];
  });
  // Keep page boundaries intact: Horizon derives the next page from its last card.
  const pages = new Map();
  for (const product of products) {
    const page = product.item.dataset.page || 'home';
    if (!pages.has(page)) pages.set(page, []);
    pages.get(page).push(product);
  }
  const sorted = [...pages.values()].flatMap(page => rankProducts(state, page));
  if (!sorted.some((product, index) => product.item !== products[index].item)) return;
  // Disconnect while moving existing nodes, so our own reorder cannot trigger a loop.
  const observer = observers.get(grid);
  observer?.disconnect();
  for (const product of sorted) grid.appendChild(product.item);
  observer?.observe(grid, { childList: true });
}

function enhance(root) {
  if (explicitOrder()) return;
  const grids = [...root.querySelectorAll('.resource-list--grid, .product-grid[role="list"]')];
  for (const grid of grids) {
    reorderGrid(grid);
    if (!observers.has(grid)) {
      const observer = new MutationObserver(() => reorderGrid(grid));
      observer.observe(grid, { childList: true });
      observers.set(grid, observer);
    }
  }
  for (const [grid, observer] of observers) {
    if (!grid.isConnected) { observer.disconnect(); observers.delete(grid); }
  }
}

function allowedCategories() {
  return new Set([...document.querySelectorAll('[data-industromart-machine-item] a[href*="/collections/"]')].map(link => {
    try { return decodeURIComponent(new URL(link.href).pathname.split('/collections/')[1]?.split('/')[0] || ''); }
    catch { return ''; }
  }).filter(Boolean));
}

async function refreshHome() {
  const target = document.querySelector('[data-im-interest-list]');
  if (!target || context.page !== 'index' || document.documentElement.classList.contains('shopify-design-mode')) return;
  const category = topCategory(state, allowedCategories());
  if (!category || target.dataset.imInterestCategory === category) return;
  const current = ++generation;
  request?.abort();
  request = new AbortController();
  const url = new URL((window.Shopify?.routes?.root || '/') + 'collections/' + encodeURIComponent(category), location.origin);
  url.searchParams.set('view', 'im-interest');
  const preview = new URLSearchParams(location.search).get('preview_theme_id');
  if (preview) url.searchParams.set('preview_theme_id', preview);
  try {
    const response = await fetch(url, { signal: request.signal, credentials: 'same-origin' });
    if (!response.ok) return;
    const html = await response.text();
    if (current !== generation || !target.isConnected) return;
    const section = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-im-interest-list]');
    const next = section?.querySelector('.resource-list--grid');
    const old = target.querySelector('.resource-list--grid');
    if (!next || !old || !next.querySelector('product-card[data-product-id]')) return;
    observers.get(old)?.disconnect();
    observers.delete(old);
    old.replaceWith(document.importNode(next, true));
    target.dataset.imInterestCategory = category;
    const header = target.querySelector('.section-resource-list__header');
    const heading = header?.querySelector('h2, h3, .text-block > p');
    if (heading && target.dataset.imInterestTitle) heading.textContent = target.dataset.imInterestTitle;
    const viewAll = header?.querySelector('a[href*="/collections/"]');
    if (viewAll) {
      const link = new URL(url);
      link.searchParams.delete('view');
      viewAll.href = link.href;
    }
    if (header && !header.querySelector('[data-im-reset-interest]')) {
      const reset = document.createElement('button');
      reset.type = 'button';
      reset.className = 'button button-unstyled im-interest-reset';
      reset.dataset.imResetInterest = '';
      reset.textContent = target.dataset.imResetLabel;
      reset.addEventListener('click', () => {
        state = readState(null);
        try { sessionStorage.removeItem(key); } catch {}
        location.reload();
      });
      header.appendChild(reset);
    }
    enhance(target);
  } catch (error) {
    if (error.name !== 'AbortError') console.warn('[IndustroMart] Default product list retained.');
  }
}

document.addEventListener(StandardEvents.cartLinesUpdate, event => {
  if (event.action !== 'add' || !event.promise) return;
  const source = event.target instanceof Element ? event.target.closest('product-card') : null;
  const categories = source?.dataset.imCollections?.split('|');
  const type = source?.dataset.imProductType;
  event.promise.then(({ detail }) => {
    if (detail?.didError) return;
    const card = detail?.productId ? document.querySelector('product-card[data-product-id="' + CSS.escape(String(detail.productId)) + '"]') : null;
    record(categories || card?.dataset.imCollections?.split('|') || context.productCollections,
      type || card?.dataset.imProductType || context.productType, 5);
    // Keep add-to-cart feedback immediate; update merchandising after the browser can paint it.
    requestAnimationFrame(() => { enhance(document); refreshHome(); });
  }).catch(() => {});
});
document.addEventListener('shopify:section:load', event => { enhance(event.target); refreshHome(); });
document.addEventListener(StandardEvents.collectionUpdate, () => requestAnimationFrame(() => enhance(document)));
window.addEventListener('pagehide', () => {
  request?.abort();
  for (const observer of observers.values()) observer.disconnect();
  observers.clear();
});
window.addEventListener('pageshow', event => { if (event.persisted) { enhance(document); refreshHome(); } });
enhance(document);
refreshHome();
